import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BaseForm } from 'components'
import _ from 'lodash'

import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import Button from 'devextreme-react/button'
import CheckBox from 'devextreme-react/check-box'

import { IxField } from 'components/forms/field'

import { useAppDispatch } from 'store'
import { RootState } from 'slices'
import { setFilterValues, setFilterLookupValue, setValuesFromDB, setDirectLoad, setLoad, setActiveDivRef } from 'slices/auftrag-slice.gen'
import { setGridFilters } from 'slices/pages-slice'

import { setMaxLabelWidth } from 'helpers/set-max-label-width'
import { keyboardControl } from 'helpers'
import { useIxFocusElement } from 'hooks'

import { IxLookupProvider, useIxLookupContext } from 'contexts/lookup-context'
import {
    IxNcremUserLookup,
    IxTeamLookup,
} from 'components/forms/lookups'

import {
    SelectInput,
    TextInput,
} from 'components/forms'

import { IxAuftragStatusFilter, IxAuftragStatusFilterLabels } from 'enums/auftrag.gen/auftrag-status-filter-enum.gen'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

const _t = (s: string) => s
const labelsMap: {[k: string]: string} = {
    STATUSTYPEFILTER: _t('auftrag:field.STATUSTYPEFILTER'),
    TEAMFILTER: _t('auftrag:field.TEAMFILTER'),
    USERFILTER: _t('auftrag:field.USERFILTER'),
    MELDERFILTER: _t('auftrag:field.MELDERFILTER'),
    NRBETRFILTER: _t('auftrag:field.NRBETRFILTER'),
}

const lookupFields: any[] = [
    'TEAMFILTER',
    'USERFILTER',
]

/* v8 ignore start */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const enumToOpts = (e: any, labels: any, t: Function) => {
    const opts = []

    for(let x in e) {
        if (!isNaN(Number(x)))
            continue

        opts.push({
            name: t(labels[e[x]]),
            value: e[x]
        })
    }

    return opts
}
/* v8 ignore end */

interface IAutoUpdateForm {
    pageName?: string,
    itemsData: any[],
    lastFocusedElement: any,
    divRef: any
}

export const AutoUpdateForm = ({ pageName, itemsData, lastFocusedElement, divRef }: IAutoUpdateForm) => {
    const dispatch = useAppDispatch()

    const { values, setFieldValue } = useFormikContext<any>()
    const { setFieldValue: setLookupFieldValue, fieldHasCallbacks } = useIxLookupContext()
    const activeDivRef = useSelector((state: RootState) => state.auftragSlice.activeDivRef)

    const filterValues = useSelector((state: RootState) => state.auftragSlice.filterValues)
    const filterLookupValues = useSelector((state: RootState) => state.auftragSlice.filterLookupValues)
    const valuesFromDB = useSelector((state: RootState) => state.auftragSlice.valuesFromDB)

    const prevFilterLookupValues = useRef<any>({})
    const prevFilterValues = useRef<any>({})
    const firstRender = useRef<any>(true)


    useIxFocusElement(values, itemsData, lastFocusedElement, activeDivRef, divRef)

    useEffect(() => {
        for(const [key, value] of Object.entries(filterLookupValues)) {
            if (!_.isEqual(prevFilterLookupValues.current[key], value) && fieldHasCallbacks(key)) {
                setLookupFieldValue(key, (value as any), false, true)
                prevFilterLookupValues.current[key] = value
            }
        }
    }, [filterLookupValues, setLookupFieldValue, fieldHasCallbacks])

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            if (!pageName) { return }
        }

        if (!_.isEqual(prevFilterValues.current, filterValues)) { return }

        const vals = Object.keys(values)
            .filter(k => !lookupFields.includes(k))
            .reduce((obj: any, key: string) => {
                obj[key] = values[key]
                return obj
            }, {})

        const _filterValues = Object.keys(filterValues)
            .filter(k => !lookupFields.includes(k))
            .reduce((obj: any, key: string) => {
                obj[key] = filterValues[key]
                return obj
            }, {})

        if (!_.isEqual(_filterValues, vals)) {
            dispatch(setFilterValues(vals))
            prevFilterValues.current = vals
        }
    }, [dispatch, values, filterValues, pageName])

    useEffect(() => {
        for(const [key, value] of Object.entries(filterValues)) {
            if ((valuesFromDB || lookupFields.indexOf(key) === -1) && prevFilterValues.current[key] !== value) {
                setFieldValue(key, value, false)
            }
        }

        dispatch(setValuesFromDB(false))
        prevFilterValues.current = filterValues
    }, [filterValues, setFieldValue, dispatch, valuesFromDB])

    useEffect(() => {
        pageName && dispatch(setGridFilters({ name: pageName, filterValues, filterLookupValues }))
    }, [dispatch, filterValues, filterLookupValues, pageName])

    return null
}

export type IIxAuftragGridFilterContent = {
    pageName?: string,
    loadingEnabled: boolean,
    visible: boolean,
}

export const IxAuftragGridFilterContent = ({ pageName, loadingEnabled, visible }: IIxAuftragGridFilterContent) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const { values } = useFormikContext<any>()

    const divRef = useRef<HTMLDivElement>(null)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))

    const itemsData = useMemo(() => ([
        { name: 'STATUSTYPEFILTER', index: 1, type: 'field' },
        { name: 'TEAMFILTER', index: 1, type: 'field' },
        { name: 'USERFILTER', index: 1, type: 'field' },
        { name: 'MELDERFILTER', index: 0, type: 'field' },
        { name: 'NRBETRFILTER', index: 0, type: 'field' },

        { name: 'DIRECT_LOAD', index: 0, type: 'field' },
        { name: t('global:button.load'), index: 0, type: 'button' }
    ]), [t])

    const lastFocusedElement = useRef<any>(null)
    const onKeyboardControl = keyboardControl(itemsData, t, divRef, lastFocusedElement)

    const directLoad = useSelector((state: RootState) => state.auftragSlice.directLoad)


    const prevWidth = useRef<any>(0)

    const onDirectLoadChange = useCallback((e: any) => {
        dispatch(setDirectLoad(e.value))
    }, [dispatch])

    useEffect(() => {
            const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')
            filterElement?.addEventListener('keydown', onKeyboardControl)

            return () => {
                filterElement?.removeEventListener('keydown', onKeyboardControl)
            }
        },[onKeyboardControl])

    useEffect(() => {
        const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')

        const setPrevFocusedElement = (e: any) => {
            const filterButton = e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]')
            dispatch(setActiveDivRef(e.target.closest('.dx-drawer-content') || e.target.closest('.dx-drawer-panel-content')))

            if (e.target.tagName === 'DIV' && !e.target.className.includes('dropdowneditor') && !e.target.className.includes('button') && !e.target.closest('.dx-radiobutton') && filterButton) {
                setTimeout(() => {
                    e.target.closest('.dx-drawer-panel-content').querySelector('[aria-label="filter"]').focus()
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').classList.remove('dx-state-focused')
                }, 0)
            }

            if (e.target.tagName === 'INPUT' || e.target.tagName === 'I' || (typeof e.target.className === 'string' && e.target.className.includes('dropdowneditor'))) {
                lastFocusedElement.current = e.target
                filterElement?.querySelector('.dx-state-focused.dx-radiobutton')?.classList.remove('dx-state-focused')
                filterElement?.querySelector('.dx-checkbox.dx-state-focused')?.classList.remove('dx-state-focused')
            }
        }
        filterElement?.addEventListener('mousedown', setPrevFocusedElement)

        return () => {
            filterElement?.removeEventListener('mousedown', setPrevFocusedElement)
        }
    }, [dispatch])

    useEffect(() => {
        if (visible) {
            const formElement: any = divRef.current

            if (formFieldType === 'label-inside-max-field-width' || formFieldType === 'label-left-max-field-width') {
                setTimeout(() => {
                    setMaxLabelWidth(formElement)
                }, 500)
            }
            else {
                formElement?.style.removeProperty('--max-labels-width')
            }
        }

    },[visible, formFieldType])

    const screenByWidth = useCallback((_: any) => {
        const width: any = divRef.current?.getBoundingClientRect().width || 0

        if (width < 500) {
            return 'xs'
        } else {
            return 'lg'
        }
    }, [])

    const onOptionChanged = useCallback((e: any) => {
        const width = divRef.current?.getBoundingClientRect().width

        if (prevWidth.current !== width) {
            e.component.repaint()
            prevWidth.current = width
        }
    },[])

    const onLookupFieldChange = useCallback((name: string, value: any) => {
        dispatch(setFilterLookupValue({ name, value, formValue: values[name] }))
    }, [dispatch, values])

    return (
        <div ref={ divRef } style={ visible ? {} : { height: 0, overflow: 'hidden' } }>
            <IxLookupProvider onFieldChanged={ onLookupFieldChange }>
                <AutoUpdateForm pageName={ pageName } itemsData={ itemsData } lastFocusedElement={lastFocusedElement} divRef={divRef}/>
                <ResponsiveBox screenByWidth={ screenByWidth } onOptionChanged={(e) => onOptionChanged(e)}>
                    {/* layout goes here */}
                    <Row />
                    <Row />
                    <Row screen='xs sm'/>
                    <Row screen='xs'/>
                    <Row screen='xs'/>
                    <Row screen = 'xs'/>

                    <Col />
                    <Col screen='sm md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='sm md lg' ratio={ 0.5 }/>

                    <Item>
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='md lg' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ SelectInput }
                                name='STATUSTYPEFILTER'
                                label={ t(labelsMap['STATUSTYPEFILTER']) }
                                active={ loadingEnabled }
                                opts={ enumToOpts(IxAuftragStatusFilter, IxAuftragStatusFilterLabels, t) }
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 0 } col={ 1 } colspan={ 1 } screen='md lg' />
                        <Location row={ 0 } col={ 1 } colspan={ 1 } screen='sm' />
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ IxTeamLookup }
                                name='TEAMFILTER'
                                label={ t(labelsMap['TEAMFILTER']) }
                                active={ loadingEnabled }
                                selectionMode='multi'
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 0 } col={ 2 } colspan={ 1 } screen='md lg' />
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 2 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ IxNcremUserLookup }
                                name='USERFILTER'
                                label={ t(labelsMap['USERFILTER']) }
                                active={ loadingEnabled }
                                selectionMode='multi'
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='md lg' />
                        <Location row={ 1 } col={ 1 } colspan={ 1 } screen='sm' />
                        <Location row={ 3 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ TextInput }
                                name='MELDERFILTER'
                                label={ t(labelsMap['MELDERFILTER']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 1 } col={ 1 } colspan={ 1 } screen='md lg' />
                        <Location row={ 2 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 4 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ TextInput }
                                name='NRBETRFILTER'
                                label={ t(labelsMap['NRBETRFILTER']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>

                    <Item>
                        <Location row={ 0 } col={ 4 } colspan={ 1 } rowspan={ 2 } screen='md lg' />
                        <Location row={ 0 } col={ 2 } colspan={ 1 } rowspan={ 3 } screen='sm' />
                        <Location row={ 5 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='rb-item mb-1'>
                            <div className='mb-1'>
                                <IxField
                                    as={ CheckBox }
                                    name='DIRECT_LOAD'
                                    text={ t('global:label.directLoad') }
                                    onValueChanged={ onDirectLoadChange }
                                    value={ directLoad }
                                />
                            </div>
                            <Button disabled={ directLoad } width='100%' text={t('global:button.load')} onClick={ () => dispatch(setLoad(true)) }/>
                        </div>
                    </Item>
                </ResponsiveBox>
            </IxLookupProvider>
        </div>
    )
}

export type IIxAuftragGridFilter = {
    pageName?: string,
    loadingEnabled?: boolean,
    visible?: boolean,
}

export const IxAuftragGridFilter = ({ pageName, loadingEnabled=true, visible=true }: IIxAuftragGridFilter) => {
    const formik = useMemo(() => ({
        initialValues: {
            "STATUSTYPEFILTER": null,
            "TEAMFILTER": null,
            "USERFILTER": null,
            "MELDERFILTER": null,
            "NRBETRFILTER": null
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
    }), [])

    return (
        <BaseForm {...formik}>
            <IxAuftragGridFilterContent pageName={ pageName } loadingEnabled={ loadingEnabled } visible={ visible } />
        </BaseForm>
    )
}
